import { ChevronDown } from 'lucide-react';
import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';

type DropdownOption = string | { label: string; value: string };

interface CustomDropdownProps {
  data?: DropdownOption[];
  width?: string;
  placeholder?: string;
  onSelect?: (value: string) => void;
  className?: string;
  selectedValue?: string;
  buttonHeight?: number;
  disabled?: boolean;
}

export interface CustomDropdownRef {
  open: () => void;
  close: () => void;
  toggle: () => void;
  getSelectedValue: () => string | null;
  clearSelection: () => void;
}

export const CustomDropdown = forwardRef<CustomDropdownRef, CustomDropdownProps>(
  (
    {
      data = [],
      width = 'w-64',
      placeholder = 'Select an option',
      onSelect,
      className = '',
      selectedValue,
      buttonHeight = '12',
      disabled = false
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const [openUpward, setOpenUpward] = useState(false);

    // 🔠 Helper to capitalize first letter
    const capitalizeFirst = (str: string) =>
      str ? str.charAt(0).toUpperCase().replace(/_/g, ' ') + str.slice(1).replace(/_/g, ' ') : '';

    const updatePosition = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const estimatedHeight = Math.min(data.length * 36, 240); // Max height is 60rem/max-h-60
        const spaceBelow = window.innerHeight - rect.bottom;
        const shouldOpenUp = spaceBelow < estimatedHeight + 20;

        setOpenUpward(shouldOpenUp);
        setDropdownPos({
          top: shouldOpenUp
            ? rect.top + window.scrollY - 4
            : rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        updatePosition();
        setIsOpen(true);
      },
      close: () => setIsOpen(false),
      toggle: () => {
        if (disabled) return;
        if (!isOpen) updatePosition();
        setIsOpen(!isOpen);
      },
      getSelectedValue: () => selectedValue || null,
      clearSelection: () => {
        if (onSelect) onSelect('');
      },
    }));

    useEffect(() => {
      if (isOpen) {
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
      }
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen]);

    const portalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          dropdownRef.current && !dropdownRef.current.contains(target) &&
          portalRef.current && !portalRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!isOpen) return;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setSelectedIndex((prev) => (prev < data.length - 1 ? prev + 1 : 0));
            break;
          case 'ArrowUp':
            event.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : data.length - 1));
            break;
          case 'Enter':
            event.preventDefault();
            if (selectedIndex >= 0) {
              const item = data[selectedIndex];
              const value = typeof item === 'string' ? item : item.value;
              handleSelect(value);
            }
            break;
          case 'Escape':
            setIsOpen(false);
            break;
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, selectedIndex, data]);

    const handleSelect = (value: string): void => {
      if (disabled) return;
      setIsOpen(false);
      if (onSelect) {
        onSelect(value);
      }
    };

    const handleButtonClick = (): void => {
      if (disabled) return;
      if (!isOpen) {
        updatePosition();
      }
      setIsOpen(!isOpen);
    };

    const getLabelFromValue = (val: string | undefined): string => {
      if (!val) return placeholder;
      const match = data.find((item) => {
        if (typeof item === 'string') return item === val;
        return item.value === val;
      });
      return typeof match === 'string' ? match : match?.label || placeholder;
    };

    const displayValue = getLabelFromValue(selectedValue);

    return (
      <div className={`relative ${width} ${className}`} ref={dropdownRef}>
        <button
          type="button"
          className={`
            w-full h-${buttonHeight} px-4 pr-3
            bg-slate-900 border border-slate-700/50 rounded-lg
            text-white text-left text-sm
            hover:bg-slate-800 hover:border-slate-600
            focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500
            transition-all duration-200
            flex items-center justify-between gap-2
            ${isOpen ? 'ring-2 ring-orange-500/50 border-orange-500 bg-slate-800' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5] pointer-events-none' : ''}
          `}
          onClick={handleButtonClick}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span
            className={`truncate ${selectedValue ? 'text-white font-semibold' : 'text-slate-200'
              }`}
          >
            {capitalizeFirst(displayValue)}
          </span>

          <ChevronDown
            className={`w-4 h-4 text-slate-200 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180 text-orange-500' : ''
              }`}
          />
        </button>

        {isOpen && dropdownPos.width > 0 && typeof document !== 'undefined' && createPortal(
          <div
            ref={portalRef}
            className="fixed z-[10001] bg-gray-800 border border-gray-800 shadow-2xl rounded-xl overflow-hidden animate-in fade-in duration-75"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              transform: openUpward ? 'translateY(-100%)' : 'none'
            }}
          >
            <div className="py-1 max-h-60 overflow-y-auto custom-scroll">
              {data.length === 0 ? (
                <div className="px-4 py-3 text-slate-500 text-xs italic text-center">
                  No options available
                </div>
              ) : (
                data.map((item: DropdownOption, index) => {
                  const label = typeof item === 'string' ? item : item.label;
                  const value = typeof item === 'string' ? item : item.value;
                  const isSelected = selectedValue === value;

                  return (
                    <button
                      key={index}
                      type="button"
                      className={`
                        w-full text-left px-4 py-2.5 text-xs font-semibold
                        transition-all duration-150 hover:bg-slate-600/50 text-white
                        ${isSelected ? 'bg-orange-600 !text-white' : ''}
                      `}
                      onClick={() => handleSelect(value)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      {capitalizeFirst(label)}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }
);

CustomDropdown.displayName = 'CustomDropdown';
